import * as Yup from "yup";
import { CHAR_LIMIT, CHAR_MIN } from "../../../../utils/Common/Constant";
import {
  limitMsg,
  minMsg,
} from "../../../../Components/Common/ErrorMessage/ErrorMessage";
import { NewProjectFormValues, ReviewItem, ReviewSection } from "./types";

export const STEP_LABELS = [
  "Project Type",
  "Project Info",
  "Contacts",
  "Lender",
  "Requirements",
  "Review",
];

export const FIELD_ORDER: (keyof NewProjectFormValues | string)[] = [
  "projectType",
  "projName",
  "projValue",
  "projDesc",
  "projAddr",
  "ownerId",
  "ownerPrimaryContact",
  "reqSigner",
  "reqPhone",
  "reqEmail",
  "reqAddr",
  "gcId",
  "gcPrimaryContact",
  "ownerRep",
  "otherEntities",
  "lenderBankId",
  "lenderBranchId",
  "lendingOfficerId",
  "lenderEmail",
  "preconDate",
  "requirements.lienLimitAmount",
  "requirements.lienLimitType",
  "requirements.equityAmount",
  "requirements.equityDesc",
  "requirements.constructionReportAuthoringFeeAmount",
  "requirements.preConstructionReportFeeAmount",
];

export const INITIAL_VALUES: NewProjectFormValues = {
  projectType: "",
  projName: "",
  projValue: "",
  projDesc: "",
  projAddr: "",
  projCoords: null,
  ownerName: "",
  ownerPrimaryContact: "",
  ownerPhone: "",
  ownerEmail: "",
  ownerAddr: "",
  reqSigner: "",
  reqPhone: "",
  reqEmail: "",
  reqAddr: "",
  gcName: "",
  gcPrimaryContact: "",
  gcPhone: "",
  gcEmail: "",
  gcAddr: "",
  architect: "",
  architectName: "",
  ownerRep: "",
  otherEntities: "",
  bankName: "",
  bankLocation: "",
  lenderAddr: "",
  lenderName: "",
  lenderPhone: "",
  lenderEmail: "",
  ccList: [],
  preconDate: "",
  specialReqs: "",
  ownerId: "",
  gcId: "",
  lenderBankId: "",
  lenderBranchId: "",
  lendingOfficerId: "",
  requirements: {
    mtg: "no",
    lien: "no",
    lienLimit: "no",
    lienLimitAmount: "",
    lienLimitType: "",
    notar: "no",
    subInv: "no",
    precon: "no",
    equity: "no",
    equityAmount: "",
    equityDesc: "",
    soft: "no",
    changeOrder: "no",
    changeOrderThreshold: "",
    retainage: "no",
    retainagePercentage: "",
    contingency: "no",
    contingencyAmount: "",
    constructionReportAuthoringFeeAmount: "",
    preConstructionReportFeeAmount: "",
  },
};

const requiredText = (label: string) =>
  Yup.string().trim().required(`${label} is required.`);

const limitedText = (label: string, max: number) =>
  requiredText(label).max(max, limitMsg(max));

const optionalPhone = () =>
  Yup.string()
    .test(
      "min-if-filled",
      minMsg(CHAR_MIN.CONTACT),
      (val) => {
        if (!val) return true;
        const digits = val.replace(/\D/g, "");
        if (digits.length <= 3) return true; // only country code, treat as empty
        return val.length >= CHAR_MIN.CONTACT;
      },
    )
    .max(CHAR_LIMIT.PHONE, limitMsg(CHAR_LIMIT.PHONE));

export const stepSchemas = [
  Yup.object({
    projectType: Yup.string().required(
      "Please select a project type to continue.",
    ),
  }),
  Yup.object({
    projName: limitedText("Project Name", CHAR_LIMIT.NAME),
    projValue: limitedText("Contract Value", CHAR_LIMIT.SHORT),
    projDesc: limitedText("Project Description", CHAR_LIMIT.DESCRIPTION),
    projAddr: limitedText("Project Address", CHAR_LIMIT.ADDRESS),
  }),
  Yup.object({
    ownerId: Yup.string().required("Please select an Owner."),
    ownerPrimaryContact: Yup.string().max(
      CHAR_LIMIT.NAME,
      limitMsg(CHAR_LIMIT.NAME),
    ),
    reqSigner: limitedText("Requisition Signer name", CHAR_LIMIT.NAME),
    reqPhone: optionalPhone(),
    reqEmail: Yup.string().max(CHAR_LIMIT.EMAIL, limitMsg(CHAR_LIMIT.EMAIL)),
    reqAddr: Yup.string().max(CHAR_LIMIT.ADDRESS, limitMsg(CHAR_LIMIT.ADDRESS)),
    gcId: Yup.string().required("Please select a General Contractor."),
    gcPrimaryContact: Yup.string().max(
      CHAR_LIMIT.NAME,
      limitMsg(CHAR_LIMIT.NAME),
    ),
    ownerRep: Yup.string().max(CHAR_LIMIT.NAME, limitMsg(CHAR_LIMIT.NAME)),
    otherEntities: Yup.string().max(
      CHAR_LIMIT.DESCRIPTION,
      limitMsg(CHAR_LIMIT.DESCRIPTION),
    ),
  }),
  Yup.object({
    lenderBankId: Yup.string().required("Please select a Bank."),
    lenderBranchId: Yup.string().required("Please select a Branch."),
    lendingOfficerId: Yup.string().required("Please select a Lending Officer."),
    ccList: Yup.array().of(Yup.string()),
  }),
  Yup.object({
    preconDate: Yup.string().when("requirements.precon", {
      is: "yes",
      then: (schema) =>
        schema.required("Please enter the Pre-Construction report date."),
      otherwise: (schema) => schema,
    }),
    specialReqs: Yup.string().max(
      CHAR_LIMIT.DESCRIPTION,
      limitMsg(CHAR_LIMIT.DESCRIPTION),
    ),
    requirements: Yup.object({
      lienLimitAmount: Yup.string().when("lienLimit", {
        is: "yes",
        then: (schema) => schema.required("Limit Amount is required."),
        otherwise: (schema) => schema,
      }),
      lienLimitType: Yup.string().when("lienLimit", {
        is: "yes",
        then: (schema) => schema.required("Type is required."),
        otherwise: (schema) => schema,
      }),
      equityAmount: Yup.string().when("equity", {
        is: "yes",
        then: (schema) => schema.trim().required("Amount is required."),
        otherwise: (schema) => schema,
      }),
      equityDesc: Yup.string().when("equity", {
        is: "yes",
        then: (schema) =>
          schema.trim().required("Please describe the equity contribution."),
        otherwise: (schema) => schema,
      }),
      changeOrderThreshold: Yup.string().when("changeOrder", {
        is: "yes",
        then: (schema) => schema.trim().required("Approval threshold is required."),
        otherwise: (schema) => schema,
      }),
      constructionReportAuthoringFeeAmount: Yup.string()
        .trim()
        .required("Construction report authoring fee is required."),
      preConstructionReportFeeAmount: Yup.string()
        .trim()
        .required("Pre-construction report fee is required."),
    }),
  }),
  Yup.object(),
];

export const fullSchema = stepSchemas
  .slice(0, 5)
  .reduce((acc, schema) => acc.concat(schema), Yup.object({}));

export const STEP_FIELDS: Record<number, string[]> = {
  1: ["projectType"],
  2: ["projName", "projValue", "projDesc", "projAddr"],
  3: [
    "ownerId",
    "ownerPrimaryContact",
    "reqSigner",
    "reqPhone",
    "reqEmail",
    "reqAddr",
    "gcId",
    "gcPrimaryContact",
    "ownerRep",
    "otherEntities",
  ],
  4: ["lenderBankId", "lenderBranchId", "lendingOfficerId"],
  5: [
    "preconDate",
    "requirements.lienLimitAmount",
    "requirements.lienLimitType",
    "requirements.equityAmount",
    "requirements.equityDesc",
    "requirements.changeOrderThreshold",
    "requirements.constructionReportAuthoringFeeAmount",
    "requirements.preConstructionReportFeeAmount",
  ],
  6: [],
};

export const buildReviewData = (values: NewProjectFormValues): ReviewSection[] => {
  const reviewItem = (label: string, value: string, multiline = false): ReviewItem => ({
    label,
    value: value?.trim?.() ? value.trim() : value || "-",
    multiline,
  });
  const yesNo = (v: string) => (v === "yes" ? "Yes" : v === "no" ? "No" : "-");

  return [
    {
      title: "Project Type",
      items: [
        reviewItem(
          "Type",
          values.projectType
            ? values.projectType.charAt(0).toUpperCase() +
                values.projectType.slice(1)
            : "",
        ),
      ],
    },
    {
      title: "Project Information",
      items: [
        reviewItem("Project Name", values.projName),
        reviewItem("Contract Value", values.projValue),
        reviewItem("Address", values.projAddr),
        reviewItem("Description", values.projDesc),
      ],
    },
    {
      title: "Owner",
      items: [
        reviewItem("Owner Name", values.ownerName),
        reviewItem("Primary Contact", values.ownerPrimaryContact),
        reviewItem("Phone", values.ownerPhone),
        reviewItem("Email", values.ownerEmail),
      ],
    },
    {
      title: "General Contractor",
      items: [
        reviewItem("Contractor", values.gcName),
        reviewItem("Primary Contact", values.gcPrimaryContact),
        reviewItem("Phone", values.gcPhone),
        reviewItem("Email", values.gcEmail),
      ],
    },
    {
      title: "Lender",
      items: [
        reviewItem("Bank Name", values.bankName),
        reviewItem("Branch / Location", values.bankLocation),
        reviewItem("Lending Officer", values.lenderName),
        reviewItem("Phone", values.lenderPhone),
        reviewItem("Email", values.lenderEmail),
        reviewItem("Address", values.lenderAddr),
      ],
    },
    {
      title: "Other Contacts",
      items: [
        reviewItem("Requisition Signer", values.reqSigner),
        reviewItem("Architect", values.architectName),
        reviewItem("Owner's Rep", values.ownerRep),
        reviewItem("Other Entities", values.otherEntities),
        reviewItem("CC on Reports", Array.isArray(values.ccList) ? values.ccList.join(", ") : values.ccList),
      ],
    },
    {
      title: "Requirements",
      items: [
        reviewItem("Monthly Meeting Required", yesNo(values.requirements.mtg)),
        reviewItem("Track Lien Waivers", yesNo(values.requirements.lien)),
        reviewItem("Lien Waiver Limit", yesNo(values.requirements.lienLimit)),
        ...(values.requirements.lienLimit === "yes"
          ? [
              reviewItem("Limit Amount", `$${values.requirements.lienLimitAmount}`),
              reviewItem("Limit Type", values.requirements.lienLimitType),
            ]
          : []),
        reviewItem("Notarized Lien Waivers", yesNo(values.requirements.notar)),
        reviewItem("Sub-Contractor Invoices", yesNo(values.requirements.subInv)),
        reviewItem("Pre-Construction Review", yesNo(values.requirements.precon)),
        ...(values.requirements.precon === "yes"
          ? [reviewItem("Pre-Con Report Date", values.preconDate)]
          : []),
        reviewItem("Equity Contribution", yesNo(values.requirements.equity)),
        ...(values.requirements.equity === "yes"
          ? [
              reviewItem("Equity Amount", `$${values.requirements.equityAmount}`),
              reviewItem("Equity Description", values.requirements.equityDesc, true),
            ]
          : []),
        reviewItem("Track Soft Costs", yesNo(values.requirements.soft)),
        reviewItem("Lender Approval for Change Orders", yesNo(values.requirements.changeOrder)),
        reviewItem("Retainage Withheld", yesNo(values.requirements.retainage)),
        ...(values.requirements.retainage === "yes"
          ? [reviewItem("Retainage Rate", `${values.requirements.retainagePercentage}%`)]
          : []),
        reviewItem("Lender Contingency Budget", yesNo(values.requirements.contingency)),
        ...(values.requirements.contingency === "yes"
          ? [reviewItem("Contingency Amount", `$${values.requirements.contingencyAmount}`)]
          : []),
        reviewItem(
          "Construction Report Authoring Fee (Per Report)",
          `$${values.requirements.constructionReportAuthoringFeeAmount}`,
        ),
        reviewItem(
          "Pre-Construction Report Fee",
          `$${values.requirements.preConstructionReportFeeAmount}`,
        ),
        ...(values.specialReqs
          ? [reviewItem("Special Requirements", values.specialReqs, true)]
          : []),
      ],
    },
  ];
};
